import React from 'react';
import {Box, Card, useTheme} from '@mui/material';
import {
  useGridApiRef,
  GridToolbarColumnsButton,
  GridColDef
} from '@mui/x-data-grid-premium';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import moment from 'moment';

import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import CustomPagination from '../table-custom-pagination/TableCustomPagination';
import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import ListSubheaderComponent from '../list-subheader/ListSubheader';

import {NoRowsOverlay, GridCellRender} from '../../grid-utils';
import EncryptUtils from 'src/utils/EncryptUtils';
import {APP} from 'src/styles/variables';
import {fCurrency} from 'src/utils/format-number';
import {rowStyles} from '@shared/utils/HelperUtils';
import {getGridSx} from '../../util';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import {useBoolean} from 'src/hooks/use-boolean';

const BatchLedger = (props: any) => {
  const theme = useTheme();
  const gridApiRef = useGridApiRef();
  const isMounted = React.useRef(false);
  const batchLedger = useBoolean();
  const ledgerSummaryBool = useBoolean();

  const [batchListingData, setBatchListingData] = React.useState<any>([]);
  const [batchListingGridLoading, setBatchListingGridLoading] =
    React.useState(false);
  const [ledgerSummary, setLedgerSummary] = React.useState<any>([]);
  const [summaryGridLoading, setSummaryGridLoading] = React.useState(false);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [pageCount, setPageCount] = React.useState(1);
  const [totalRecords, setTotalRecords] = React.useState(1);

  const getBatchLedger = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        batchId: props?.batchId,
        batchType: props?.batchInfo?.BatchType,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        paymentConfig: props?.paymentConfig,
        postingLevel: props?.batchInfo?.PostingLevel
      });
      setBatchListingGridLoading(true);
      const res = await APIService.getTransactionList({tk});
      setBatchListingData(res.data.batches);
      setTotalRecords(res.totalRecords);
      setPageCount(res.pageCount);
      setBatchListingGridLoading(false);
    } catch (e) {
      setBatchListingGridLoading(false);
    }
  };

  const getBatchLedgerSummary = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        batchId: props?.batchId,
        batchType: props?.batchInfo?.BatchType,
        paymentConfig: props?.paymentConfig,
        postingLevel: props?.batchInfo?.PostingLevel
      });
      setSummaryGridLoading(true);
      const res = await APIService.getTransactionListSummary({tk});
      setLedgerSummary(res.data);
      setSummaryGridLoading(false);
    } catch (e) {
      setSummaryGridLoading(false);
    }
  };

  React.useEffect(() => {
    getBatchLedger();
    getBatchLedgerSummary();
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (isMounted.current) {
      getBatchLedger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Render Pagination
  const Pagination = () => {
    return (
      <CustomPagination
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        pageCount={pageCount}
        totalRecordsCount={totalRecords}
        totalRows={batchListingData.length}
        setPage={setPage}
        setPageSizes={setPageSize}
      />
    );
  };

  const CustomToolbar = () => {
    return (
      <Box sx={{display: 'flex', justifyContent: 'flex-start', padding: '4px'}}>
        <GridToolbarColumnsButton />
      </Box>
    );
  };

  const batchListingColumns: GridColDef[] = [
    {
      field: 's_no',
      headerName: 'S.No',
      type: 'number',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 50,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: any) => (
        <GridCellRender
          value={params.api.getAllRowIds().indexOf(params.id) + 1}
        />
      )
    },
    {
      headerName: 'Invoice Number',
      field: 'InvoiceNumber',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Payment Date',
      field: 'DepositDate',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
          }
        />
      )
    },
    {
      headerName: 'Charge',
      field: 'Charge',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Allowed',
      field: 'Allowed',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'Adjustment',
      headerName: 'Adjustment',
      sortable: false,
      filterable: false,
      flex: 1,
      width: 100,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Payment',
      field: 'Payment',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Collection Agency Payment',
      field: 'CollectionAgencyPayment',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Transaction Code',
      field: 'TransactionCode',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 100,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Tracking Description',
      field: 'TrackingDescription',
      sortable: false,
      filterable: false,
      flex: 0,
      width: 150,
      align: 'left',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    }
  ];

  const ledgerSummaryColumns: GridColDef[] = [
    {
      headerName: 'Charge',
      field: 'Charge',
      sortable: false,
      filterable: false,
      flex: 1,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Allowed',
      field: 'Allowed',
      sortable: false,
      filterable: false,
      flex: 1,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      field: 'Adjustment',
      headerName: 'Adjustment',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Payment',
      field: 'Payment',
      sortable: false,
      filterable: false,
      flex: 1,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    },
    {
      headerName: 'Collection Agency Payment',
      field: 'CollectionAgencyPayment',
      sortable: false,
      filterable: false,
      flex: 1,
      align: 'left',
      renderCell: (params: any) => (
        <GridCellRender value={fCurrency(params.value)} />
      )
    }
  ];

  const renderDataGridComponent = (gridType: string) => {
    let gridLoading = batchListingGridLoading;
    let gridColumns = batchListingColumns;
    let gridRows = batchListingData;
    let pagination = true;
    let horizontalScroll = true;
    let hideFooter = false;
    if (gridType === 'summary') {
      gridLoading = summaryGridLoading;
      gridColumns = ledgerSummaryColumns;
      gridRows = ledgerSummary;
      pagination = false;
      hideFooter = true;
      horizontalScroll = false;
    }
    return (
      <StripedDataGrid
        showColumnVerticalBorder
        showCellVerticalBorder
        autoHeight
        disableRowGrouping
        disableRowSelectionOnClick
        disableColumnSelector
        disableAggregation
        checkboxSelection={false}
        apiRef={gridApiRef}
        loading={gridLoading}
        columns={gridColumns}
        rows={gridRows}
        rowThreshold={0}
        rowHeight={37}
        columnHeaderHeight={42}
        hideFooter={hideFooter}
        pagination={pagination}
        paginationMode="server"
        sortingMode="server"
        sortingOrder={['desc', 'asc']}
        filterMode="server"
        getRowId={(row: any) => row.id || row.ID}
        slotProps={{
          filterPanel: {
            sx: {'& .MuiDataGrid-filterFormValueInput': {width: 'auto'}}
          }
        }}
        getRowClassName={(params: any) => {
          return rowStyles(params);
        }}
        localeText={{
          toolbarColumns: 'Manage Columns'
        }}
        sx={getGridSx({
          theme,
          APP,
          gridHeight: 'auto',
          checkboxColor: props?.checkboxColor,
          horizontalScroll: horizontalScroll,
          minHeight: '100px'
        })}
        slots={{
          detailPanelExpandIcon: ExpandMoreOutlined,
          detailPanelCollapseIcon: ExpandLessOutlined,
          pagination: Pagination,
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <NoRowsOverlay
              noDataFound
              theme={theme}
              loading={batchListingGridLoading}
            />
          ),
          noResultsOverlay: () => (
            <NoRowsOverlay
              noRowsFound
              theme={theme}
              loading={batchListingGridLoading}
            />
          )
        }}
      />
    );
  };

  return (
    <Box sx={{width: '100%'}}>
      <ListSubheaderComponent
        onClose={props?.onClose}
        label={props?.paymentPageLabel}
      />
      <Box px={3} mt={1}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <DropDownPanel
            title={`Transaction List - ${props?.batchInfo?.BatchName}`}
            collapse={batchLedger.value}
            onCollapse={batchLedger.onToggle}
            secondaryTitle={
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <LoadingButton
                  type="submit"
                  variant="outlined"
                  onClick={() => {
                    getBatchLedger();
                    getBatchLedgerSummary();
                  }}
                  sx={{
                    backgroundColor: theme.palette.primary.light
                  }}
                  loadingPosition="start">
                  Refresh
                </LoadingButton>
              </Box>
            }>
            {renderDataGridComponent('ledger')}
          </DropDownPanel>
        </Card>
      </Box>
      <Box px={3} mt={2}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <DropDownPanel
            title={'Transaction List Summary'}
            collapse={ledgerSummaryBool.value}
            onCollapse={ledgerSummaryBool.onToggle}>
            {renderDataGridComponent('summary')}
          </DropDownPanel>
        </Card>
      </Box>
    </Box>
  );
};

export default BatchLedger;
